/**
 * "Last branded admin login" cookie.
 *
 * Records the org slug from the *last* `/admin/login/{slug}` page a staff user
 * successfully authenticated through. Used by `signOutAdminAndGetLoginPath` to
 * send the user back to that same branded page on logout, instead of the slug
 * derived from their first `user_role_assignments` row.
 *
 * Why a cookie (not `user_role_assignments`):
 *
 * `getAdminHomeOrgSlug` infers the "home" slug from the user's first
 * org-scoped role assignment, which is correct for the common case but breaks
 * for three SSO scenarios reported on UAT (issue #706 follow-up):
 *
 *   1. A *global admin* signs in via a tenant's branded SSO. The
 *      `getAdminHomeOrgSlug` global-admin precedence (#617) intentionally
 *      returns `null`, so they land at slug-less `/admin/login` even though
 *      they entered via `/admin/login/{tenant-slug}`.
 *   2. A multi-tenant user already provisioned against Org A signs in via
 *      Org B's SSO. The SSO callback does not add a new assignment row, so
 *      the helper returns Org A's slug.
 *   3. Any non-SSO branded password sign-in by a multi-tenant or global user
 *      hits exactly the same mismatch.
 *
 * This cookie captures *how the user actually authenticated* — which is the
 * correct UX answer to "where should logout send them?" — while leaving the
 * `user_role_assignments` walk as a fallback for users who arrived via the
 * slug-less global login.
 *
 * Threat model:
 *
 * The cookie carries only a public org slug. It is intentionally **not**
 * `httpOnly` so `signOutAdminAndRedirect` can consult it client-side without
 * an extra round-trip during the sign-out race window (#617). An XSS attacker
 * who could write a forged cookie could only nudge the user toward a
 * different branded login page after logout — both targets land on the same
 * Supabase auth, so there is no privilege escalation. We still validate the
 * slug against a strict allowlist on every read so a tampered value cannot
 * smuggle path traversal or scheme injection into `/admin/login/{slug}`.
 */

export const BRANDED_LOGIN_SLUG_COOKIE = 'last_admin_login_slug';

/** 30 days. Long enough to survive routine sessions; not "forever". */
export const BRANDED_LOGIN_SLUG_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

/**
 * Org-slug allowlist: lowercase ASCII, digits, hyphen; 1–63 chars; must start
 * with an alphanumeric. Mirrors the DNS-label shape used by every other
 * branded-login surface (and rejects `..`, `/`, control chars, scheme
 * delimiters, etc.).
 */
const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}$/;

export function isValidBrandedLoginSlug(value: unknown): value is string {
  return typeof value === 'string' && SLUG_PATTERN.test(value);
}

// ---------------------------------------------------------------------------
// Client-side helpers (document.cookie)
// ---------------------------------------------------------------------------

/**
 * Read the cookie from `document.cookie`. Returns the slug only if it parses
 * and matches the allowlist; any other value (missing, malformed,
 * URL-decode error, off-allowlist) yields `null`.
 *
 * SSR-safe: returns `null` when `document` is undefined.
 */
export function readBrandedLoginSlugFromDocument(): string | null {
  if (typeof document === 'undefined') return null;
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (const part of parts) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    if (part.slice(0, eq) !== BRANDED_LOGIN_SLUG_COOKIE) continue;
    try {
      const value = decodeURIComponent(part.slice(eq + 1));
      return isValidBrandedLoginSlug(value) ? value : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Write the cookie via `document.cookie`. No-op when called on the server,
 * with an invalid slug, or when `window` is unavailable.
 *
 * `Secure` is set when the page is being served over HTTPS so the cookie is
 * never emitted over a plaintext transport on a real deployment.
 */
export function writeBrandedLoginSlugToDocument(slug: string): void {
  if (typeof document === 'undefined') return;
  if (!isValidBrandedLoginSlug(slug)) return;
  const secure =
    typeof window !== 'undefined' && window.location?.protocol === 'https:'
      ? '; Secure'
      : '';
  document.cookie =
    `${BRANDED_LOGIN_SLUG_COOKIE}=${encodeURIComponent(slug)}` +
    `; Max-Age=${BRANDED_LOGIN_SLUG_MAX_AGE_SECONDS}` +
    `; Path=/; SameSite=Lax${secure}`;
}

/**
 * Clear the cookie. Called in two places:
 *
 *   1. `signOutAdminAndGetLoginPath` — once the slug has been captured and the
 *      session has ended, the cookie is dropped so the lifecycle is "set on
 *      successful login → cleared on logout" rather than living to the
 *      `Max-Age`. This closes the edge case where a user switches login
 *      methods (e.g. slug-less social OAuth after a previous branded sign-in)
 *      and would otherwise carry a stale slug.
 *   2. The slug-less `/admin/login` password handler, as defence-in-depth so
 *      a user who explicitly chose the global login is not stuck on a branded
 *      URL chosen on a previous visit (e.g. session expiry without an
 *      explicit logout).
 */
export function clearBrandedLoginSlugFromDocument(): void {
  if (typeof document === 'undefined') return;
  document.cookie =
    `${BRANDED_LOGIN_SLUG_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
}
